// App.tsx — root router for the openscan SPA. The shared Shell from lib/web
// supplies the navbar, the Darkly/Flatly theme toggle and the fluid page
// container. Routes: library (/), capture (/scan), viewer (/scans/:id),
// lens profiles (/devices),
// markers (/markers), MCP help (/mcp) and the OIDC login hand-off. Scan
// routes sit behind RequireAuth, except the viewer, which also opens for a
// signed-out guest holding a share link; the two help pages are public.
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BsBox } from 'react-icons/bs';
import { OidcLoginPage, Shell } from 'common';
import type { ShellNavItem } from 'common';
import { RequireAuth, RequireScanAccess } from './components/RequireAuth';
import { logout, useAuth } from './hooks/useAuth';
import CapturePage from './pages/CapturePage';
import DevicesPage from './pages/DevicesPage';
import LibraryPage from './pages/LibraryPage';
import MarkersPage from './pages/MarkersPage';
import McpPage from './pages/McpPage';
import ViewerPage from './pages/ViewerPage';

function Root() {
  const auth = useAuth();

  // A signed-in user without the library capability (openscan.user) can only
  // open scans shared with them, so the capture and device pages are hidden.
  const library = !auth.authenticated || auth.capabilities.library;
  const navItems: ShellNavItem[] = [
    { label: 'Library', href: '/' },
    ...(library ? [{ label: 'Scan', href: '/scan' }, { label: 'Devices', href: '/devices' }] : []),
    { label: 'Markers', href: '/markers' },
    { label: 'MCP', href: '/mcp' },
  ];

  const brand = (
    <>
      <BsBox size={18} /> openscan
    </>
  );

  return (
    <Shell
      brand={brand}
      navItems={navItems}
      userEmail={auth.authenticated ? auth.email : undefined}
      userPhotoUrl={auth.photoUrl || undefined}
      onLogout={auth.authenticated && auth.email !== 'dev@local' ? logout : undefined}
    >
      <Routes>
        <Route path="/" element={<RequireAuth auth={auth}><LibraryPage library={auth.capabilities.library} compute={auth.capabilities.compute} /></RequireAuth>} />
        <Route path="/scan" element={<RequireAuth auth={auth}><CapturePage /></RequireAuth>} />
        <Route path="/scans/:id" element={<RequireScanAccess auth={auth}><ViewerPage signedIn={auth.authenticated} /></RequireScanAccess>} />
        <Route path="/devices" element={<RequireAuth auth={auth}><DevicesPage /></RequireAuth>} />
        <Route path="/markers" element={<MarkersPage />} />
        <Route path="/mcp" element={<McpPage />} />
        {/* returnTo is pinned to "/" — landing back on /login would loop. */}
        <Route path="/login" element={<OidcLoginPage returnTo="/" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  );
}
